import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Actions, ofActionSuccessful, Selector, State, StateContext, Store } from '@ngxs/store';
import { iif, of } from 'rxjs';
import { debounceTime, groupBy, mergeMap, switchMap } from 'rxjs/operators';
import { SetSingleTagTypingState } from './single-call-tag-behaviour.actions';

export interface SingleCallTagBehaviourStateModel {
   [instanceId: string]: SingleCallTagBehaviourInstance;
}

export interface SingleCallTagBehaviourInstance {
   isTyping: boolean;
}

export function defaultSingleCallTagBehaviourInstance(): SingleCallTagBehaviourInstance {
   return {
      isTyping: false,
   };
}

@State<SingleCallTagBehaviourStateModel>({
   name: 'singleCallTagBehaviour',
   defaults: {},
})
@Injectable()
export class SingleCallTagBehaviourState {
   constructor(private store: Store, private actions$: Actions) {
      this.actions$
         .pipe(
            ofActionSuccessful(SetSingleTagTypingState),
            groupBy((x: SetSingleTagTypingState) => x.brickInstanceId),
            mergeMap((group) =>
               group.pipe(
                  debounceTime(1000),
                  switchMap((x: SetSingleTagTypingState) =>
                     iif(() => x.isTyping, this.store.dispatch(new SetSingleTagTypingState(x.brickInstanceId, false)), of(null))
                  )
               )
            )
         )
         .subscribe();
   }

   private static getSpecificBehaviourStateOrDefault(state: SingleCallTagBehaviourStateModel, brickInstanceId: string): SingleCallTagBehaviourInstance {
      return state[brickInstanceId] ?? defaultSingleCallTagBehaviourInstance();
   }

   @Selector([SingleCallTagBehaviourState])
   public static getSingleCallTagState(state: SingleCallTagBehaviourStateModel) {
      return (brickInstanceId: string) => {
         return this.getSpecificBehaviourStateOrDefault(state, brickInstanceId);
      };
   }

   @Selector([SingleCallTagBehaviourState.getSingleCallTagState])
   public static isTyping(getSingleCallTagState: (brickInstanceId: string) => SingleCallTagBehaviourInstance) {
      return (brickInstanceId: string) => {
         return getSingleCallTagState(brickInstanceId).isTyping;
      };
   }

   @Action(SetSingleTagTypingState)
   @ImmutableContext()
   public setSingleTagTypingState(ctx: StateContext<SingleCallTagBehaviourStateModel>, action: SetSingleTagTypingState) {
      ctx.setState((state: SingleCallTagBehaviourStateModel) => {
         const brickInstance = SingleCallTagBehaviourState.getSpecificBehaviourStateOrDefault(state, action.brickInstanceId);
         brickInstance.isTyping = action.isTyping;
         state[action.brickInstanceId] = brickInstance;
         return state;
      });
   }
}
